import React, { Fragment, useCallback, useState, useEffect } from "react";
import { backend, API_URL } from "../../../config";
import {
  Button,
  Typography,
  Modal,
  CircularProgress,
  Container,
  Grid,
  TextField,
  InputLabel,
  Autocomplete,
} from "@mui/material";

import DatePicker from "@mui/lab/DatePicker";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import LocalizationProvider from "@mui/lab/LocalizationProvider";

import ManagementNav from "../../../features/OrderManagement/components/Nav/ManagementNav";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const MergeOrders = (props) => {
  const classes = useStyles();

  const dDate = new Date();

  // Get next valid work day
  const dDateDay = dDate.getDay();
  const daysToAdd = dDateDay === 5 ? 3 : dDateDay === 6 ? 2 : 1;
  dDate.setDate(dDate.getDate() + daysToAdd);

  const [confirmModal, setConfirmModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState([dDate, dDate]);
  const [orders, setOrders] = useState([]);
  const [ordersMatched, setOrdersMatched] = useState([]);
  const [orderSelected, setOrderSelected] = useState(null);
  const [ordersToMerge, setOrdersToMerge] = useState([]);
  const [ordersInvoiced, setOrdersInvoiced] = useState([]);

  const handleChangeOrderSelected = (e, value) => {
    setOrderSelected(value);
  };

  const handleChangeOrdersToMerge = (e, value) => {
    setOrdersToMerge(value);
  };

  const handleChangeDate = (e) => {
    const { name, value } = e.target;

    setOrders([]);
    setOrderSelected(null);
    setOrdersToMerge([]);

    setDeliveryDate((prevState) => {
      let deliveryDateRange = [...prevState];

      if (name === "start_date") {
        deliveryDateRange[0] = value;
      } else {
        deliveryDateRange[1] = value;
      }

      return deliveryDateRange;
    });
  };

  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const confirmModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to merge the selected orders?
          <br />
          <br />
          <div align="right">
            <Button
              onClick={() => toggleConfirmModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Merging Orders <br />
              This may take some time relative to the number of orders and
              orderitems
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while merging orders
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The orders were merged successfully.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const resolveRequest = () => {
    if (finishedFlag) {
      setOrderSelected(null);
      setOrdersToMerge([]);
      setOrdersInvoiced([]);
      setOrdersMatched([]);
      getOrders();
    }
    setLoadingFlag(false);
    setFinishedFlag(false);
    toggleConfirmModal();
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    const payloadObj = {
      base_order: orderSelected.id,
      orders_to_merge: ordersToMerge.map((item) => item.id),
    };

    await backend
      .post(`${API_URL}mergeorders/`, payloadObj)
      .then(({ data }) => resolveRequest())
      .catch((error) => {
        console.log(error);
        const errorData = error.response.data;

        setErrorInfo((prevState) => {
          let newMsg =
            typeof errorData === "object"
              ? errorData.hasOwnProperty("message")
                ? errorData.message
                : errorData.detail
              : error.message;

          if (newMsg !== "") {
            const errorMsg =
              prevState.message +
              `${prevState.message === "" ? "" : "<br />"}` +
              newMsg;

            return {
              error: true,
              message: errorMsg,
            };
          }

          return prevState;
        });
      });
  };

  const getOrders = useCallback(async () => {
    console.log("getORders");
    let deliveryStartDateObj = new Date(deliveryDate[0]);

    const deliveryStartDateParam = new Date(
      deliveryStartDateObj.getTime() -
        deliveryStartDateObj.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];

    let deliveryEndDateObj = new Date(deliveryDate[1]);

    const deliveryEndDateParam = new Date(
      deliveryEndDateObj.getTime() -
        deliveryEndDateObj.getTimezoneOffset() * 60000,
    )
      .toISOString()
      .split("T")[0];

    await backend
      .get(
        `${API_URL}orders?no_page=1&start_date=${deliveryStartDateParam}&end_date=${deliveryEndDateParam}`,
      )
      .then(({ data }) => {
        if (data.length) {
          let oInvoiced = [];

          data.forEach((item) => {
            if (item.invoiced) {
              oInvoiced.push(
                `${item.customer ? item.customer.username : "Anonymous"} #${
                  item.id
                }`,
              );
            }
          });

          const dataFiltered = data.filter((item) => !item.invoiced);

          setOrders(dataFiltered);
          setOrdersInvoiced(oInvoiced);
        }
      });
  }, [deliveryDate]);

  useEffect(() => {
    getOrders();
  }, [deliveryDate, getOrders]);

  useEffect(() => {
    const getOrdersMatched = () => {
      const ordersFiltered = orders.filter(
        (item) =>
          item.id !== orderSelected.id &&
          item?.company_location?.id === orderSelected?.company_location?.id,
      );

      setOrdersMatched(ordersFiltered);
    };

    if (orderSelected !== null) {
      getOrdersMatched();
    }
  }, [orders, orderSelected]);

  return (
    <Container style={{ minHeight: "80vh" }}>
      <Modal open={confirmModal} onClose={toggleConfirmModal}>
        {confirmModalContent}
      </Modal>
      <ManagementNav {...props} />
      <Typography component="h6" variant="h6" align="center">
        Merge Orders
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Select the orders you would like to use as a base then select the
          orders to merge into base
        </Grid>
        <Grid item xs={12} sm={12}></Grid>
        <Grid item xs={12} sm={12}>
          Load orders from a given date range.
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change start date",
              }}
              value={deliveryDate[0]}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery start date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "start_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              value={deliveryDate[1]}
              renderInput={(props) => (
                <TextField
                  {...props}
                  fullWidth
                  label="Delivery end date"
                  variant="standard"
                />
              )}
              onChange={(date) =>
                handleChangeDate({
                  target: {
                    name: "end_date",
                    value: date,
                  },
                })
              }
            />
          </LocalizationProvider>
        </Grid>
        {deliveryDate[0] !== null && deliveryDate[1] !== null ? (
          <>
            <Grid item xs={6} sm={6}>
              <InputLabel shrink id="orderBase">
                Order (base)
              </InputLabel>
              <Autocomplete
                fullWidth
                disablePortal
                size="small"
                name="orderBase"
                options={orders}
                getOptionLabel={(option) =>
                  `${
                    option.customer ? option.customer.username : "Anonymous"
                  } #${option.id}`
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {`${
                      option.customer ? option.customer.username : "Anonymous"
                    } #${option.id}`}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handleChangeOrderSelected(e, value)}
                helpertext="Select the order to use as base for order merge."
                value={orderSelected}
              />
            </Grid>
            <Grid item xs={6} sm={6}>
              <InputLabel shrink id="ordersToMerge">
                Orders to merge
              </InputLabel>
              <Autocomplete
                fullWidth
                multiple
                disablePortal
                size="small"
                name="ordersToMerge"
                options={ordersMatched}
                getOptionLabel={(option) =>
                  `${
                    option.customer ? option.customer.username : "Anonymous"
                  } #${option.id}`
                }
                renderInput={(params) => (
                  <TextField {...params} size="small" variant="standard" />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    {`${
                      option.customer ? option.customer.username : "Anonymous"
                    } #${option.id}`}
                  </li>
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(e, value) => handleChangeOrdersToMerge(e, value)}
                helpertext="Select the orders to merge with base order."
                value={ordersToMerge}
                disabled={orderSelected === null}
              />
            </Grid>
            {ordersInvoiced.length > 0 && (
              <Grid item xs={12}>
                <b>There are {ordersInvoiced.length} orders invoiced.</b>
              </Grid>
            )}
          </>
        ) : (
          <></>
        )}
        <Grid item xs={12} sm={12} style={{ textAlign: "center" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => toggleConfirmModal()}
            className={classes.button}
            disabled={
              orderSelected === null || !ordersToMerge.length || loadingFlag
            }
          >
            Merge Orders
          </Button>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MergeOrders;
